import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, createInvestment, updateInvestment } from "@/lib/api";
import {
  investmentCreateSchema,
  type Investment,
  type InvestmentCreate,
} from "@finance/shared";

interface InvestmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investment: Investment | null;
  onSaved: () => void;
}

interface FormState {
  name: string;
  ticker: string;
  quantity: string;
  cost_basis: string;
  current_price: string;
}

const EMPTY: FormState = {
  name: "",
  ticker: "",
  quantity: "",
  cost_basis: "",
  current_price: "",
};

function fromInvestment(investment: Investment | null): FormState {
  if (!investment) return EMPTY;
  return {
    name: investment.name,
    ticker: investment.ticker ?? "",
    quantity: String(investment.quantity),
    cost_basis: String(investment.cost_basis),
    current_price: String(investment.current_price),
  };
}

export function InvestmentFormDialog({
  open,
  onOpenChange,
  investment,
  onSaved,
}: InvestmentFormDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(fromInvestment(investment));
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [open, investment]);

  const isEdit = investment !== null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    const fieldErrors: Record<string, string> = {};

    const quantity = Number(form.quantity);
    if (form.quantity === "" || Number.isNaN(quantity)) {
      fieldErrors.quantity = "Enter a valid number";
    }

    const costBasis = Number(form.cost_basis);
    if (form.cost_basis === "" || Number.isNaN(costBasis)) {
      fieldErrors.cost_basis = "Enter a valid number";
    }

    const currentPrice = Number(form.current_price);
    if (form.current_price === "" || Number.isNaN(currentPrice)) {
      fieldErrors.current_price = "Enter a valid number";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const trimmedTicker = form.ticker.trim();
    const payload: InvestmentCreate = {
      name: form.name.trim(),
      ticker: trimmedTicker === "" ? null : trimmedTicker.toUpperCase(),
      quantity,
      cost_basis: costBasis,
      current_price: currentPrice,
    };

    const parsed = investmentCreateSchema.safeParse(payload);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!flat[key]) flat[key] = issue.message;
      }
      setErrors(flat);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      if (isEdit && investment) {
        await updateInvestment(investment.id, parsed.data);
      } else {
        await createInvestment(parsed.data);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Failed to save investment");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit holding" : "Add holding"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this manually tracked investment holding."
              : "Track a stock, ETF, or fund holding with cost basis and current price."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="investment-name">Name</Label>
            <Input
              id="investment-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Vanguard Total Stock Market"
              autoFocus
              required
            />
            {errors.name ? (
              <p className="text-xs text-destructive">{errors.name}</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="investment-ticker">Ticker (optional)</Label>
            <Input
              id="investment-ticker"
              value={form.ticker}
              onChange={(e) => update("ticker", e.target.value)}
              placeholder="VTI"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="font-mono uppercase"
            />
            {errors.ticker ? (
              <p className="text-xs text-destructive">{errors.ticker}</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="investment-quantity">Quantity</Label>
            <Input
              id="investment-quantity"
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              value={form.quantity}
              onChange={(e) => update("quantity", e.target.value)}
              placeholder="10"
              required
            />
            {errors.quantity ? (
              <p className="text-xs text-destructive">{errors.quantity}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="investment-cost">Cost basis (per share)</Label>
              <Input
                id="investment-cost"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.cost_basis}
                onChange={(e) => update("cost_basis", e.target.value)}
                placeholder="100.00"
                required
              />
              {errors.cost_basis ? (
                <p className="text-xs text-destructive">{errors.cost_basis}</p>
              ) : null}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="investment-price">Current price (per share)</Label>
              <Input
                id="investment-price"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.current_price}
                onChange={(e) => update("current_price", e.target.value)}
                placeholder="120.00"
                required
              />
              {errors.current_price ? (
                <p className="text-xs text-destructive">{errors.current_price}</p>
              ) : null}
            </div>
          </div>

          {submitError ? (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              role="alert"
            >
              {submitError}
            </p>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Add holding"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
